import _define_property from "@swc/helpers/src/_define_property.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import cx from "classnames";
import { datadogRum } from "@datadog/browser-rum";
import { Primary, ScreenReaderText, SvgIcon, Text } from "@cinch-labs/design-system";
import { useCompareBar } from "./provider/compare-bar-provider";
import styles from "./compare-bar.module.css";
import { useAnalytics } from "@cinch-nx/shared-analytics";
import { compareBarDrawerInteractionEvent, compareCtaClickEvent, compareVehicleRemovalEvent } from "./tracking/tracking-events";
var CompareBarImage = function(param) {
    var src = param.src, isSold = param.isSold;
    var ref = useState(false), hasError = ref[0], updateErrorState = ref[1];
    var updateError = function() {
        updateErrorState(true);
    };
    return !src || hasError ? /*#__PURE__*/ _jsx("div", {
        className: styles.compareBarHoldingImage,
        "aria-hidden": "true",
        children: /*#__PURE__*/ _jsx(SvgIcon, {
            name: "vehicle-side",
            size: 22,
            color: "mercury",
            className: styles.compareBarImageIcon
        })
    }) : /*#__PURE__*/ _jsx("img", {
        className: cx(styles.compareBarCarImage, _define_property({}, styles.compareBarCarImageSold, isSold)),
        alt: "",
        role: "presentation",
        src: src || "",
        onError: updateError,
        loading: "lazy",
        width: 60,
        height: 34
    });
};
export var CompareBar = function() {
    var ref = useCompareBar(), comparisonVehicles = ref.comparisonVehicles, compareBarExpanded = ref.compareBarExpanded, compareBarSticky = ref.compareBarSticky, maxNumberOfItems = ref.maxNumberOfItems, vehicleSummaryMap = ref.vehicleSummaryMap, removeVehicle = ref.removeVehicle, setCompareBarExpanded = ref.setCompareBarExpanded, customerHasActiveOrder = ref.customerHasActiveOrder;
    var trackEvent = useAnalytics().trackEvent;
    var compareButtonDisabled = comparisonVehicles.length <= 1;
    var vehicleIds = comparisonVehicles.map(function(vehicle) {
        return vehicle.vehicleId;
    });
    var compareCtaUrl = "/compare-cars/".concat(vehicleIds.join(";"));
    var handleVehicleRemoval = function(vehicleId, vehicleMake, vehicleModel) {
        removeVehicle(vehicleId);
        trackEvent(compareVehicleRemovalEvent(vehicleMake, vehicleModel, comparisonVehicles));
        // TODO: Transfer ownership to PD
        datadogRum.addAction("Compare - Compare Bar vehicle removed", {
            service: "compare",
            squad: "search",
            vehicle: {
                vehicleId: vehicleId,
                make: vehicleMake,
                model: vehicleModel
            }
        });
    };
    var renderVehicleCard = function(vehicle) {
        var ref;
        var vehicleId = vehicle.vehicleId, vehicleTitle = vehicle.vehicleTitle, vehicleImage = vehicle.vehicleImage, vehicleMake = vehicle.vehicleMake, vehicleModel = vehicle.vehicleModel;
        var isSold = (vehicleSummaryMap === null || vehicleSummaryMap === void 0 ? void 0 : (ref = vehicleSummaryMap[vehicleId]) === null || ref === void 0 ? void 0 : ref.isAvailable) === false;
        return /*#__PURE__*/ _jsx("li", {
            className: styles.compareBarListItem,
            children: /*#__PURE__*/ _jsxs("article", {
                className: styles.compareBarListArticle,
                children: [
                    /*#__PURE__*/ _jsx(CompareBarImage, {
                        src: vehicleImage,
                        isSold: isSold
                    }),
                    /*#__PURE__*/ _jsx(Text, {
                        element: "h3",
                        "aria-describedby": isSold ? "vehicle-sold-".concat(vehicleId) : undefined,
                        className: cx(styles.compareBarListItemHeading, _define_property({}, styles.compareBarListItemHeadingSold, isSold)),
                        children: vehicleTitle
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        className: styles.compareBarListItemButton,
                        type: "button",
                        onClick: function() {
                            return handleVehicleRemoval(vehicleId, vehicleMake, vehicleModel);
                        },
                        "aria-label": "Remove ".concat(vehicleTitle, " from the compare list"),
                        children: /*#__PURE__*/ _jsx(SvgIcon, {
                            name: "cross",
                            size: 16,
                            color: "deepPurple"
                        })
                    }),
                    isSold && /*#__PURE__*/ _jsx(Text, {
                        element: "span",
                        fontSizeFixed: "s",
                        fontWeight: "bold",
                        className: styles.compareBarListItemSoldBadge,
                        "aria-hidden": "true",
                        children: "Sold"
                    }),
                    isSold && /*#__PURE__*/ _jsx(ScreenReaderText, {
                        "aria-hidden": "true",
                        id: "vehicle-sold-".concat(vehicleId),
                        children: "This vehicle has been sold"
                    })
                ]
            })
        }, vehicleId);
    };
    var renderCompareBarItems = function(_, index) {
        var currentVehicle = comparisonVehicles[index];
        if (currentVehicle) {
            return renderVehicleCard(currentVehicle);
        }
        return /*#__PURE__*/ _jsx("li", {
            className: cx(styles.compareBarListItem, styles.compareBarListItemEmpty),
            children: "You can add another car to compare"
        }, index);
    };
    var handleCompareBarToggle = function(open) {
        setCompareBarExpanded(open);
        trackEvent(compareBarDrawerInteractionEvent(open));
    };
    var handleCompareCtaClick = function() {
        trackEvent(compareCtaClickEvent(comparisonVehicles));
        // TODO: Transfer ownership to PD
        datadogRum.addAction("Compare - Bar CTA clicked", {
            service: "compare",
            squad: "search",
            compareCount: comparisonVehicles.length,
            compareVehicleIds: comparisonVehicles.map(function(vehicle) {
                return vehicle.vehicleId;
            })
        });
    };
    useEffect(function() {
        if (comparisonVehicles.length === 0) {
            setCompareBarExpanded(true);
        }
    }, [
        comparisonVehicles
    ]);
    return /*#__PURE__*/ _jsxs("section", {
        className: cx(styles.compareBar, _define_property({}, styles.compareBarUnstuck, !compareBarSticky)),
        id: "compare",
        "aria-labelledby": "compare-list-heading",
        "aria-describedby": compareBarExpanded ? "compare-list-body" : undefined,
        hidden: comparisonVehicles.length === 0 || customerHasActiveOrder,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.compareBarCollapsedView,
                children: [
                    /*#__PURE__*/ _jsxs(Text, {
                        className: styles.compareBarExpandedTitle,
                        element: "a",
                        id: !compareBarExpanded ? "compare-list-heading" : undefined,
                        hidden: compareBarExpanded,
                        children: [
                            "Your compare list",
                            " ",
                            comparisonVehicles.length > 0 && "(".concat(comparisonVehicles.length, ")")
                        ]
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        className: styles.compareBarExpansionButton,
                        onClick: function() {
                            return handleCompareBarToggle(!compareBarExpanded);
                        },
                        "aria-expanded": compareBarExpanded,
                        "aria-label": "".concat(compareBarExpanded ? "Collapse" : "Expand", " compare bar"),
                        children: /*#__PURE__*/ _jsx(SvgIcon, {
                            name: "chevron-down",
                            size: 16,
                            color: "mineShaft"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                hidden: !compareBarExpanded,
                className: cx(styles.container, _define_property({}, styles.containerExpanded, compareBarExpanded)),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.compareBarContent,
                        children: [
                            /*#__PURE__*/ _jsx(Text, {
                                className: styles.compareBarHeading,
                                element: "h2",
                                fontSize: "l",
                                fontWeight: "bold",
                                id: compareBarExpanded ? "compare-list-heading" : undefined,
                                children: "Your compare list"
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                className: styles.compareBarText,
                                id: "compare-list-body",
                                children: [
                                    "Select up to ",
                                    maxNumberOfItems,
                                    " cars to compare them side-by-side."
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Primary, {
                                "aria-describedby": compareButtonDisabled ? "compare-validation" : undefined,
                                "aria-disabled": compareButtonDisabled,
                                role: compareButtonDisabled ? "link" : undefined,
                                href: compareButtonDisabled ? undefined : compareCtaUrl,
                                element: "a",
                                classNames: {
                                    button: styles.compareBarButtonDesktop
                                },
                                onClick: handleCompareCtaClick,
                                children: "Compare cars"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("ol", {
                        className: styles.compareBarList,
                        children: Array.from({
                            length: maxNumberOfItems
                        }, renderCompareBarItems)
                    }),
                    /*#__PURE__*/ _jsx(Primary, {
                        "aria-describedby": compareButtonDisabled ? "compare-validation" : undefined,
                        "aria-disabled": compareButtonDisabled,
                        role: compareButtonDisabled ? "link" : undefined,
                        href: compareButtonDisabled ? undefined : compareCtaUrl,
                        element: "a",
                        classNames: {
                            button: styles.compareBarButtonMobile
                        },
                        isFullWidth: true,
                        onClick: handleCompareCtaClick,
                        children: "Compare cars"
                    })
                ]
            }),
            compareButtonDisabled && /*#__PURE__*/ _jsxs(ScreenReaderText, {
                id: "compare-validation",
                "aria-hidden": "true",
                children: [
                    "You need to add at least 2 cars to the compare list. You currently have ",
                    comparisonVehicles.length
                ]
            }),
            /*#__PURE__*/ _jsx("a", {
                className: styles.compareBarHiddenLink,
                href: "#results",
                children: "Go back to results"
            })
        ]
    });
};
